import { useCallback, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { captureException } from '@sentry/nextjs';
import { DocumentUploadService } from '~/src/services/document-upload.service';
import type { AxiosError, AxiosProgressEvent, AxiosResponse } from 'axios';
import type { FormNameDocumentEnum } from '~/src/models/enums/form-name.enum';
import type { PostDocumentFileInterface } from '~/src/repositories/document-upload/document-upload.repository';
import type { FileDataInterface } from '~/src/redux/documents/documents.types';

type MutationReturnedType = { data: Promise<AxiosResponse<FileDataInterface>> };

export const usePostDocumentUpload = (url: string, formName: FormNameDocumentEnum) => {
    const startValueProgress = 0;
    const [progress, setProgress] = useState(startValueProgress);
    const abortControllerRef = useRef<{[key: string]: AbortController}>({});

    const mutation = useMutation<MutationReturnedType, AxiosError,
        { file: PostDocumentFileInterface['file'], name: string }>((args) => {
            const { file, name } = args;
            abortControllerRef.current[name] = new AbortController();

            const config = {
                onUploadProgress: (event: AxiosProgressEvent) => {
                    setProgress(Math.round((event.loaded * 100) / (event?.total || 1)));
                },
                signal: abortControllerRef?.current[name].signal,
            };

            return new Promise((resolve) => resolve({
                data: DocumentUploadService.postDocumentFile({ file, formName }, config, url),
            }));
        }, {
            onError: (error) => {
                // eslint-disable-next-line no-console
                console.log('usePostDocumentUpload', error);
                captureException(error);
            },
        });

    const reset = useCallback(({ name }: { name: string }) => {
        abortControllerRef.current[name].abort();
        mutation.reset();
        setProgress(startValueProgress);
    }, [mutation.reset, setProgress]);

    return { ...mutation, progress, abortControllerRef, reset };
};
